import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Group, Text, ActionIcon, Card, Stack, Badge, ScrollArea, Tooltip, Loader, Skeleton } from '@mantine/core';
import { IconSearch, IconHeart, IconHeartFilled, IconPlus, IconMessageDots } from '@tabler/icons-react';
import chatPng from '../../../assets/chat.png';
import readingPng from '../../../assets/reading.png';
import settingPng from '../../../assets/setting.png';
import timeBgPng from '../../../assets/time_bg.png';
import homeBgDefaultPng from '../../../assets/background.png';
import { createChatAtom } from '../stores/atoms/chatAtoms';
import { messagesMapAtom, currentChatIdAtom } from '@/stores/atoms/chatAtoms';
import { activePageAtom, homeActiveCategoryAtom, homeShowGamesAtom, homeBackgroundImageAtom, homeTimeImageAtom } from '../stores/atoms/uiAtoms';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
// import { mineGamesAtom } from '@/stores/atoms/mineAtoms';
import { favoriteScriptsAtom } from '@/stores/atoms/favoriteAtoms';
import { createSessionForScript } from '@/renderer/utils/sessionSync';
import { createSessionAtom } from '@/stores/atoms/sessionAtoms';
import { fetchAdventures, fetchCategories } from '@/utils/gameApi';
import { getOrCreateUserId } from '@/utils/session';
import { currentScriptIdAtom } from '@/stores/atoms/scriptAtoms';
import SafeImage from '@/components/common/SafeImage';
import { sessionsAtom, setActiveSessionAtom } from '@/stores/atoms/sessionAtoms';
import { chatOverlayOpenAtom, chatOverlayReturnAtom, localChatModeAtom } from '@/stores/atoms/uiAtoms';
import { useT } from '@/i18n';
import { useSwipe } from '@/renderer/utils/gestures';
import { notifications } from '@mantine/notifications';
import { authUserAtom, authModalOpenAtom } from '@/stores/atoms/authAtoms';
import { languageAtom } from '@/stores/atoms/i18nAtoms';
import PageHeader from '@/components/common/PageHeader';
import { createOverlayOpenAtom } from '@/stores/atoms/uiAtoms';
import { useIsDesktop } from '@/renderer/utils/responsive';
import { useSlideExit } from '@/renderer/utils/pageTransitions';
// swipe gestures removed for simplicity
// Simple in-memory cache for categories and adventure lists
const CATS_TTL = 60_000; // categories: 60s is fine
const ADV_TTL = 30_000; // adventures: 30s per requirement
let catsCache = { ts: 0, data: [] };
const listCache = new Map();
const mockScripts = [
    { id: '1', title: 'INTP', description: 'Smart nerdy loner, also lil baby', author: '@Cake_chaan', image: 'https://picsum.photos/seed/1/800/600', views: '452.9k', tags: ['推荐'] },
    { id: '2', title: '2B and A2', description: '2B and A2 live with you now.', author: '@AkiraMd', image: 'https://picsum.photos/seed/2/800/600', views: '1.7m', tags: ['特色'] },
    { id: '3', title: 'Kafka', description: 'Move that body.', author: '@mina_ketchup', image: 'https://picsum.photos/seed/3/800/600', views: '435.1k', tags: ['场景'] },
    { id: '4', title: 'Anna', description: 'A shy yandere girl who kidnapped you', author: '@Jpgxht', image: 'https://picsum.photos/seed/4/800/600', views: '1.2m', tags: ['语音'] },
    { id: '5', title: 'Detective Noir', description: 'Solve mysteries in a rainy city', author: '@noir', image: 'https://picsum.photos/seed/5/800/600', views: '88.3k', tags: ['群'] },
];
// 类目与数据均由服务端提供（包含名为 all 的类目）
const Home = () => {
    const createChat = useSetAtom(createChatAtom);
    const setActivePage = useSetAtom(activePageAtom);
    const [activeCat, setActiveCat] = useAtom(homeActiveCategoryAtom);
    const [categories, setCategories] = useState([]);
    const [list, setList] = useState([]);
    const [advLoaded, setAdvLoaded] = useState(false);
    // const [mineGames, setMineGames] = useAtom(mineGamesAtom);
    const [favorites, setFavorites] = useAtom(favoriteScriptsAtom);
    const allSessions = useAtomValue(sessionsAtom);
    const [messagesMap, setMessagesMap] = useAtom(messagesMapAtom);
    const [, setCurrentChatId] = useAtom(currentChatIdAtom);
    const createSession = useSetAtom(createSessionAtom);
    const setActiveSession = useSetAtom(setActiveSessionAtom);
    // const setEditingId = useSetAtom(editingMineIdAtom);
    const setCurrentScriptId = useSetAtom(currentScriptIdAtom);
    const setChatOverlayOpen = useSetAtom(chatOverlayOpenAtom);
    const setChatOverlayReturn = useSetAtom(chatOverlayReturnAtom);
    const setLocalMode = useSetAtom(localChatModeAtom);
    const [lang, setLang] = useAtom(languageAtom);
    const [authUser] = useAtom(authUserAtom);
    const setAuthModalOpen = useSetAtom(authModalOpenAtom);
    const setCreateOpen = useSetAtom(createOverlayOpenAtom);
    const t = useT();
    const isDesktop = useIsDesktop(1024);
    const viewportRef = useRef(null);
    const scrollerRef = useRef(null);
    const homeRef = useRef(null);
    const gridRef = useRef(null);
    const scrollerHeightRef = useRef(null);
    const [showLeftFade, setShowLeftFade] = useState(false);
    const [showRightFade, setShowRightFade] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [manualRefreshing, setManualRefreshing] = useState(false);
    const manualRefreshTimerRef = React.useRef(null);
    const isIOSRef = React.useRef(() => {
        try {
            return document.documentElement.classList.contains('ios');
        }
        catch {
            return false;
        }
    });
    const [refreshSeq, setRefreshSeq] = useState(0);
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: -1 });
    const loadedIdxRef = React.useRef(new Set());
    const refreshTargetRef = React.useRef(null);
    // hint removed; keep logic simple
    const virtuosoComponents = React.useMemo(() => ({
        List: React.forwardRef(({ style, children }, ref) => (_jsx("div", { ref: ref, style: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, ...style }, className: "virt-grid-list", children: children }))),
        Item: ({ children, ...props }) => _jsx("div", { ...props, children: children }),
    }), []);
    const updateFades = React.useCallback(() => {
        const el = viewportRef.current;
        if (!el)
            return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setShowLeftFade(scrollLeft > 0);
        setShowRightFade(scrollLeft + clientWidth < scrollWidth - 1);
    }, []);
    React.useEffect(() => {
        const userId = getOrCreateUserId();
        (async () => {
            try {
                const now = Date.now();
                // Categories cache
                let cs;
                if (catsCache.ts && now - catsCache.ts < CATS_TTL && catsCache.data.length) {
                    cs = catsCache.data;
                    setCategories(cs);
                }
                else {
                    cs = await fetchCategories(userId, lang);
                    setCategories(cs || []);
                    catsCache = { ts: now, data: cs || [] };
                }
                // Pick the first category from server response
                if (!activeCat && cs && cs.length)
                    setActiveCat(cs[0].id);
            }
            catch (e) {
                // Keep previous categories to avoid empty UI; fallback will handle display
                notifications.show({ color: 'red', title: t('error.title'), message: t('error.categories.load'), withCloseButton: true });
                // Ensure we still have an active category for list fetching
                // Do not force a synthetic 'all' here; wait for categories to load
            }
        })();
    }, [lang]);
    // 当 activeCat 变化时，从服务端获取该类目的冒险列表
    React.useEffect(() => {
        const userId = getOrCreateUserId();
        // Wait for categories to load to avoid fetching with stale/unknown ids
        if (!activeCat || !categories.length)
            return;
        // Skip if activeCat is not a valid id (e.g., legacy 'mine')
        if (!categories.some((c) => c.id === activeCat))
            return;
        (async () => {
            try {
                setAdvLoaded(false);
                const key = `${activeCat}`;
                const now = Date.now();
                const cache = listCache.get(key);
                if (cache && now - cache.ts < ADV_TTL) {
                    setList(cache.data);
                    setAdvLoaded(true);
                    return;
                }
                const adv = await fetchAdventures(userId, activeCat, lang);
                setList(adv || []);
                listCache.set(key, { ts: now, data: adv || [] });
            }
            catch {
                setList([]);
                notifications.show({ color: 'red', title: t('error.title'), message: t('error.adventures.load'), withCloseButton: true });
            }
            finally {
                setAdvLoaded(true);
            }
        })();
    }, [activeCat, categories, lang]);
    const visible = useMemo(() => list, [list]);
    // Removed horizontal swipe animation states for simplicity
    // 仅展示服务端类目（无本地 fallback，以避免二次请求）
    const displayCategories = useMemo(() => categories || [], [categories]);
    // Gesture helpers
    const catIndex = React.useMemo(() => categories.findIndex((c) => c.id === activeCat), [categories, activeCat]);
    const goPrevCat = () => {
        if (!categories.length)
            return;
        const next = Math.max(0, (catIndex > 0 ? catIndex - 1 : 0));
        if (categories[next])
            setActiveCat(categories[next].id);
    };
    const goNextCat = () => {
        if (!categories.length)
            return;
        const next = Math.min(categories.length - 1, (catIndex >= 0 ? catIndex + 1 : 0));
        if (categories[next])
            setActiveCat(categories[next].id);
    };
    const doRefresh = async () => {
        try {
            try {
                console.log('[Home] doRefresh start');
            }
            catch { }
            setRefreshing(true);
            setManualRefreshing(true);
            // iOS-only: fallback auto-hide in case image onResolved doesn't fire reliably
            if (isIOSRef.current) {
                if (manualRefreshTimerRef.current) {
                    try {
                        window.clearTimeout(manualRefreshTimerRef.current);
                    }
                    catch { }
                    manualRefreshTimerRef.current = null;
                }
                manualRefreshTimerRef.current = window.setTimeout(() => {
                    setManualRefreshing(false);
                    manualRefreshTimerRef.current = null;
                }, 2000);
            }
            setRefreshSeq((s) => s + 1);
            loadedIdxRef.current = new Set();
            refreshTargetRef.current = { ...visibleRange };
            const userId = getOrCreateUserId();
            if (!categories.length) {
                const cs = await fetchCategories(userId, lang);
                setCategories(cs || []);
                catsCache = { ts: Date.now(), data: cs || [] };
            }
            if (activeCat) {
                const adv = await fetchAdventures(userId, activeCat, lang);
                setList(adv || []);
                listCache.set(`${activeCat}`, { ts: Date.now(), data: adv || [] });
            }
            // If nothing is visible, finish manual refresh immediately
            const targetRange = refreshTargetRef.current;
            const targetCount = targetRange ? Math.max(0, targetRange.end - targetRange.start + 1) : 0;
            if (targetCount === 0) {
                setManualRefreshing(false);
                if (manualRefreshTimerRef.current) {
                    try {
                        window.clearTimeout(manualRefreshTimerRef.current);
                    }
                    catch { }
                    manualRefreshTimerRef.current = null;
                }
            }
            // Removed refreshed toast per UX simplification
        }
        catch {
            // http layer already notifies
        }
        finally {
            try {
                console.log('[Home] doRefresh finished');
            }
            catch { }
            setRefreshing(false);
        }
    };
    // Swipe handlers: only bind start/end to avoid interfering with native scroll
    const swipeHandlers = useSwipe({
        threshold: 48,
        requireTopForDown: true,
        isAtTop: () => {
            const el = scrollerRef.current;
            return !el || el.scrollTop <= 2;
        },
        onDown: doRefresh,
        onLeft: goNextCat,
        onRight: goPrevCat,
    });
    // no precomputed handlers; attach inline to avoid blocking scroll
    const markLoaded = (idx) => {
        if (!manualRefreshing)
            return;
        const target = refreshTargetRef.current;
        if (!target)
            return;
        if (idx < target.start || idx > target.end)
            return;
        if (!loadedIdxRef.current.has(idx)) {
            loadedIdxRef.current.add(idx);
            const targetCount = Math.max(0, target.end - target.start + 1);
            if (loadedIdxRef.current.size >= targetCount) {
                setManualRefreshing(false);
                if (manualRefreshTimerRef.current) {
                    try {
                        window.clearTimeout(manualRefreshTimerRef.current);
                    }
                    catch { }
                    manualRefreshTimerRef.current = null;
                }
            }
        }
    };
    // Custom swipe for horizontal with visual follow
    // Touch handlers removed
    // Recalculate fade visibility when categories render changes or language toggles
    React.useEffect(() => {
        requestAnimationFrame(updateFades);
    }, [displayCategories.length, lang, updateFades]);
    // 如果历史上存在 'mine' 选中，强制切回首个服务端类目
    React.useEffect(() => {
        if (activeCat === 'mine' && categories.length) {
            setActiveCat(categories[0].id);
        }
    }, [activeCat, categories.length]);
    const isFav = (id) => favorites.some((f) => (typeof f === 'string' ? f === id : f.id === id));
    const toggleFav = (it) => {
        const id = it.id;
        if (isFav(id)) {
            setFavorites(favorites.filter((f) => (typeof f === 'string' ? f !== id : f.id !== id)));
        }
        else {
            const snap = { id, title: it.title, image_url: it.image_name || it.image_url || it.image, author: it.author };
            setFavorites([snap, ...favorites]);
        }
    };
    // Helper: open latest session if exists; otherwise create new
    const openLatestOrCreate = (s) => {
        if (!authUser) {
            setAuthModalOpen(true);
            return;
        }
        const sessionsForScript = (allSessions || []).filter((sess) => sess.scriptId === s.id);
        if (sessionsForScript.length > 0) {
            const latest = sessionsForScript.slice().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))[0];
            const title = latest.name || latest.scriptName || s.title;
            createChat(title, s.id);
            // wait a tick for current_chat_id to update, then hydrate messages map and open overlay
            setTimeout(() => {
                const id = (JSON.parse(localStorage.getItem('current_chat_id') || 'null'));
                if (id) {
                    // ensure atom state aligns with storage for immediate consumers
                    setCurrentChatId(id);
                    const history = (latest.messages || []).map((m) => ({ id: m.id, role: m.role, content: m.content, timestamp: m.timestamp }));
                    setMessagesMap({ ...messagesMap, [id]: history });
                }
                setChatOverlayReturn('home');
                setCurrentScriptId(s.id);
                setActiveSession(latest.id);
                setLocalMode(false);
                setChatOverlayOpen(true);
            }, 0);
            return;
        }
        // No save found: create new session and open overlay
        const img = s.image_name || s.image_url || s.image;
        const newSession = createSessionForScript(s.id, s.title, img, s.ui_code, s.prologue);
        createSession(newSession);
        setCurrentScriptId(s.id);
        createChat(s.title, s.id);
        setChatOverlayReturn('home');
        setLocalMode(false);
        setChatOverlayOpen(true);
    };
    // Debug logs removed
    React.useEffect(() => {
        const onScroll = () => {
            try {
                console.log('[Home] window scrollY', window.scrollY);
            }
            catch { }
        };
        try {
            window.addEventListener('scroll', onScroll, { passive: true });
        }
        catch { }
        return () => { try {
            window.removeEventListener('scroll', onScroll);
        }
        catch { } };
    }, []);
    // Extra diagnostics removed
    // Removed debug listeners and scroller probe logs
    // Probe grid dimensions versus scroller
    React.useEffect(() => {
        try {
            const grid = gridRef.current;
            const sc = scrollerRef.current;
            if (!grid || !sc)
                return;
            const rect = grid.getBoundingClientRect();
            const h = rect.height;
            console.log('[Home] grid metrics', {
                gridClientH: grid.clientHeight,
                gridScrollH: grid.scrollHeight,
                gridBoxH: h,
                scrollerClientH: sc.clientHeight,
                scrollerScrollH: sc.scrollHeight,
                items: grid.children.length,
            });
        }
        catch { }
    }, [visible.length]);
    // Dynamically constrain scroller to viewport to guarantee vertical scroll
    const computeScrollerHeight = React.useCallback(() => {
        const sc = scrollerRef.current;
        if (!sc)
            return;
        try {
            const rect = sc.getBoundingClientRect();
            const top = rect.top;
            const vh = window.innerHeight || document.documentElement.clientHeight || 0;
            // Try read bottom safe area inset if available
            const rootStyle = getComputedStyle(document.documentElement);
            const sab = parseFloat(rootStyle.getPropertyValue('--sab') || '0') || 0;
            // Footer baseline 64px as set in AppShell.Footer
            const footer = 64;
            const target = Math.max(120, Math.floor(vh - top - footer - sab));
            sc.style.maxHeight = `${target}px`;
            sc.style.height = `${target}px`;
            scrollerHeightRef.current = target;
            console.log('[Home] computeScrollerHeight', { top, vh, sab, footer, target });
        }
        catch (e) {
            // no-op
        }
    }, []);
    React.useEffect(() => {
        // Defer to next frame so layout settles
        const id = requestAnimationFrame(computeScrollerHeight);
        return () => cancelAnimationFrame(id);
    }, [computeScrollerHeight, visible.length, manualRefreshing, showLeftFade, showRightFade]);
    React.useEffect(() => {
        const onResize = () => computeScrollerHeight();
        try {
            window.addEventListener('resize', onResize, { passive: true });
        }
        catch { }
        try {
            window.addEventListener('orientationchange', onResize, { passive: true });
        }
        catch { }
        try {
            if (window.visualViewport)
                window.visualViewport.addEventListener('resize', onResize, { passive: true });
        }
        catch { }
        return () => {
            try {
                window.removeEventListener('resize', onResize);
            }
            catch { }
            try {
                window.removeEventListener('orientationchange', onResize);
            }
            catch { }
            try {
                if (window.visualViewport)
                    window.visualViewport.removeEventListener('resize', onResize);
            }
            catch { }
        };
    }, [computeScrollerHeight]);
    // Landing page (default)
    const [showGames, setShowGames] = useAtom(homeShowGamesAtom);
    const [homeBg] = useAtom(homeBackgroundImageAtom);
    const [homeTimeImg] = useAtom(homeTimeImageAtom);
    // Slide transitions (used in game list branch; keep hooks stable across renders)
    // Disable enter animation on mount (when switching via bottom nav),
    // but keep animation when entering/exiting the novel module (resetKey=showGames)
    const { startExit, containerStyle, underlayStyle, panelStyle, exiting } = useSlideExit(280, showGames, { enterOnMount: false, animateOnReset: true });
    // Debug logs to trace white screen issues
    React.useEffect(() => {
        try {
            console.log('[Home] showGames changed ->', showGames);
        }
        catch { }
    }, [showGames]);
    React.useEffect(() => {
        try {
            console.log('[Home] useSlideExit exiting ->', exiting);
        }
        catch { }
    }, [exiting]);
    const fmtTime = React.useCallback(() => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), []);
    const [now, setNow] = useState(() => fmtTime());
    useEffect(() => {
        const id = window.setInterval(() => setNow(fmtTime()), 1000);
        return () => window.clearInterval(id);
    }, [fmtTime]);
    if (!showGames) {
        const timeBg = homeTimeImg || timeBgPng;
        return (_jsxs("div", { style: { position: 'relative', height: '100%', width: '100%', overflow: 'hidden', background: `url(${homeBg || homeBgDefaultPng}) center/cover no-repeat`, display: 'flex', flexDirection: 'column', alignItems: 'center' }, children: [_jsx("div", { style: { marginTop: '10vh', width: 'min(90vw, 600px)', height: 'min(30vh, 214px)', backgroundImage: `url(${timeBg})`, backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: 24, boxShadow: '0 12px 32px rgba(0,0,0,0.12)', border: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Text, { size: "xl", fw: 800, style: { color: 'var(--app-text-primary)', fontSize: 'clamp(36px, 16vw, 96px)', lineHeight: 1.1 }, children: now }) }), _jsxs("div", { style: { marginTop: '8vh', display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 90px))', gap: 24, padding: 16 }, children: [_jsx(Card, { radius: "md", p: "md", style: { width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', boxShadow: 'none', border: 'none' }, onClick: () => { try {
                                console.log('[Home] open chat app');
                            }
                            catch { } ; setActivePage('whatsapp'); }, children: _jsxs(Stack, { gap: 6, align: "center", children: [_jsx("img", { src: chatPng, alt: "\u804A\u5929", width: 64, height: 64, style: { objectFit: 'contain' } }), _jsx(Text, { fw: 700, style: { color: 'var(--app-text-primary)' }, children: "\u804A\u5929" })] }) }), _jsx(Card, { radius: "md", p: "md", style: { width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', boxShadow: 'none', border: 'none' }, onClick: () => { try {
                                console.log('[Home] open games');
                            }
                            catch { } ; setShowGames(true); }, children: _jsxs(Stack, { gap: 6, align: "center", children: [_jsx("img", { src: readingPng, alt: "\u5C0F\u8BF4", width: 64, height: 64, style: { objectFit: 'contain' } }), _jsx(Text, { fw: 700, style: { color: 'var(--app-text-primary)' }, children: "\u5C0F\u8BF4" })] }) }), _jsx(Card, { radius: "md", p: "md", style: { width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', boxShadow: 'none', border: 'none' }, onClick: () => { try {
                                console.log('[Home] open settings');
                            }
                            catch { } ; setActivePage('settings'); }, children: _jsxs(Stack, { gap: 6, align: "center", children: [_jsx("img", { src: settingPng, alt: "\u8BBE\u7F6E", width: 64, height: 64, style: { objectFit: 'contain' } }), _jsx(Text, { fw: 700, style: { color: 'var(--app-text-primary)' }, children: "\u8BBE\u7F6E" })] }) })] })] }));
    }
    return (_jsxs("div", { style: containerStyle, children: [_jsx("div", { "aria-hidden": true, style: underlayStyle }), _jsx("div", { style: panelStyle, children: _jsxs(Stack, { gap: "md", p: "sm", className: `home-root ${isDesktop ? 'home-desktop' : 'home-mobile'}`, style: { flex: 1, minHeight: 0 }, ref: homeRef, children: [_jsx(PageHeader, { title: "\u4E92\u52A8\u5C0F\u8BF4", onBack: () => startExit(() => setShowGames(false)), rightExtra: (_jsxs(Group, { gap: "xs", children: [_jsx(ActionIcon, { variant: "light", radius: "xl", onClick: () => setActivePage('search'), "aria-label": t('search'), children: _jsx(IconSearch, { size: 18 }) }), _jsx(ActionIcon, { variant: "filled", radius: "xl", onClick: () => { if (!authUser) {
                                            setAuthModalOpen(true);
                                            return;
                                        } setActivePage('create'); }, "aria-label": t('create.title.new'), children: _jsx(IconPlus, { size: 18 }) })] })) }), _jsx("div", { className: `fade-edges ${showLeftFade ? '' : 'hide-left'} ${showRightFade ? '' : 'hide-right'}`, children: _jsx(ScrollArea, { scrollbarSize: 0, offsetScrollbars: true, type: "never", styles: { viewport: { overflowX: 'auto' } }, viewportRef: viewportRef, onScrollPositionChange: () => updateFades(), children: _jsx(CategoryTabs, { categories: displayCategories, activeId: activeCat || (displayCategories[0]?.id || null), onSelect: (id) => { setActiveCat(id); requestAnimationFrame(updateFades); } }) }) }), manualRefreshing && (_jsxs("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }, children: [_jsx(Loader, { size: "xs" }), _jsx(Text, { size: "xs", c: "dimmed", children: t('common.loading') })] })), isDesktop ? (_jsxs("div", { className: "home-masonry", style: { columnGap: 12, padding: '0 4px 12px' }, children: [visible.length === 0 && (advLoaded ? (_jsx("div", { style: { padding: '24px 8px' }, children: _jsx(Text, { size: "sm", c: "dimmed", children: t('profile.noGames') }) })) : (_jsxs("div", { children: [_jsx(Skeleton, { height: 220, radius: "md", mb: "sm" }), _jsx(Skeleton, { height: 220, radius: "md", mb: "sm" }), _jsx(Skeleton, { height: 220, radius: "md" })] }))), visible.map((s, idx) => (_jsx("div", { className: "home-masonry-item", style: { breakInside: 'avoid', marginBottom: 12 }, children: _jsxs(Card, { withBorder: true, radius: "md", padding: 0, onClick: () => openLatestOrCreate(s), className: "script-card card-soft", style: { cursor: 'pointer', position: 'relative' }, children: [_jsx("div", { style: { position: 'absolute', right: 8, top: 8, zIndex: 2 }, onClick: (e) => e.stopPropagation(), children: _jsx(Tooltip, { label: isFav(s.id) ? t('favorite.remove') : t('favorite.add'), children: _jsx(ActionIcon, { size: "sm", variant: "light", onClick: () => toggleFav(s), children: isFav(s.id) ? _jsx(IconHeartFilled, { size: 14, color: "#ff4d4f" }) : _jsx(IconHeart, { size: 14 }) }) }) }), _jsx(Card.Section, { children: _jsxs("div", { style: { width: '100%', position: 'relative', overflow: 'hidden' }, children: [_jsx(SafeImage, { src: s.image_name || s.image_url || s.image, alt: s.title, height: "auto", fit: "cover", style: { display: 'block', width: '100%' }, lazy: idx > 8, onResolved: manualRefreshing ? () => markLoaded(idx) : undefined }), _jsxs("div", { style: {
                                                                position: 'absolute',
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                padding: '8px 8px 6px',
                                                                background: 'linear-gradient(180deg, rgba(0,0,0,0.00) 0%, rgba(0,0,0,0.18) 20%, rgba(0,0,0,0.42) 50%, rgba(0,0,0,0.75) 85%, rgba(0,0,0,0.92) 97%, rgba(0,0,0,1) 100%)',
                                                                backdropFilter: 'blur(2px)',
                                                                WebkitBackdropFilter: 'blur(2px)',
                                                                pointerEvents: 'none',
                                                            }, children: [_jsx(Text, { fw: 700, size: "sm", style: { color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.6)' }, children: s.title }), _jsx(Text, { size: "xs", lineClamp: 2, style: { color: 'rgba(255,255,255,0.85)' }, children: s.description }), _jsxs(Group, { justify: "space-between", children: [_jsx(Text, { size: "xs", style: { color: 'rgba(255,255,255,0.75)' }, children: s.author }), _jsxs(Group, { gap: 4, children: [_jsx(IconMessageDots, { size: 12, color: "rgba(255,255,255,0.75)" }), _jsx(Text, { size: "xs", style: { color: 'rgba(255,255,255,0.75)' }, children: s.likes })] })] })] })] }) })] }) }, s.id)))] })) : (_jsx("div", { style: { flex: 1, minHeight: 0, touchAction: 'pan-y', display: 'flex', flexDirection: 'column' }, onTouchStart: swipeHandlers.onTouchStart, onTouchEnd: swipeHandlers.onTouchEnd, onWheel: (e) => { try {
                                console.log('[Home] wrapper onWheel', { dy: e.deltaY });
                            }
                            catch { } }, children: visible.length === 0 ? (advLoaded ? (_jsx("div", { style: { padding: '24px 8px', textAlign: 'center' }, children: _jsx(Text, { size: "sm", c: "dimmed", children: t('profile.noGames') }) })) : (_jsx("div", { className: "virt-grid-list", style: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }, children: Array.from({ length: 6 }).map((_, i) => (_jsxs(Card, { withBorder: true, radius: "md", p: 0, className: "script-card card-soft", children: [_jsx(Card.Section, { children: _jsx("div", { style: { width: '100%', aspectRatio: '5 / 8' }, children: _jsx(Skeleton, { height: "100%", radius: "sm" }) }) }), _jsxs(Stack, { gap: 4, mt: 0, p: "sm", children: [_jsx(Skeleton, { height: 14, width: "70%" }), _jsx(Skeleton, { height: 10, width: "90%" }), _jsxs(Group, { justify: "space-between", children: [_jsx(Skeleton, { height: 10, width: 60 }), _jsx(Skeleton, { height: 10, width: 30 })] })] })] }, `sk-${i}`))) }))) : (_jsx("div", { ref: scrollerRef, className: "home-scroller", style: { flex: 1, minHeight: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }, children: _jsx("div", { ref: gridRef, className: "virt-grid-list", style: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }, children: visible.map((s, idx) => (_jsxs(Card, { withBorder: true, radius: "md", padding: 0, onClick: () => openLatestOrCreate(s), className: "script-card card-soft", style: { cursor: 'pointer', position: 'relative' }, children: [_jsx("div", { style: { position: 'absolute', right: 8, top: 8, zIndex: 2 }, onClick: (e) => e.stopPropagation(), children: _jsx(Tooltip, { label: isFav(s.id) ? t('favorite.remove') : t('favorite.add'), children: _jsx(ActionIcon, { size: "sm", variant: "light", onClick: () => toggleFav(s), children: isFav(s.id) ? _jsx(IconHeartFilled, { size: 14, color: "#ff4d4f" }) : _jsx(IconHeart, { size: 14 }) }) }) }), _jsx(Card.Section, { children: _jsxs("div", { style: { width: '100%', aspectRatio: '5 / 8', position: 'relative', overflow: 'hidden' }, children: [_jsx(SafeImage, { src: s.image_name || s.image_url || s.image, alt: s.title, height: "100%", fit: "cover", lazy: idx > 5, onResolved: manualRefreshing ? () => markLoaded(idx) : undefined }), _jsxs("div", { style: {
                                                                position: 'absolute',
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                padding: '8px 8px 6px',
                                                                background: 'linear-gradient(180deg, rgba(0,0,0,0.00) 0%, rgba(0,0,0,0.18) 20%, rgba(0,0,0,0.42) 50%, rgba(0,0,0,0.75) 85%, rgba(0,0,0,0.92) 97%, rgba(0,0,0,1) 100%)',
                                                                backdropFilter: 'blur(2px)',
                                                                WebkitBackdropFilter: 'blur(2px)',
                                                                pointerEvents: 'none',
                                                            }, children: [_jsx(Text, { fw: 700, size: "sm", style: { color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.6)' }, children: s.title }), _jsx(Text, { size: "xs", lineClamp: 2, style: { color: 'rgba(255,255,255,0.85)' }, children: s.description }), _jsxs(Group, { justify: "space-between", children: [_jsx(Text, { size: "xs", style: { color: 'rgba(255,255,255,0.75)' }, children: s.author }), _jsxs(Group, { gap: 4, children: [_jsx(IconMessageDots, { size: 12, color: "rgba(255,255,255,0.75)" }), _jsx(Text, { size: "xs", style: { color: 'rgba(255,255,255,0.75)' }, children: s.likes })] })] })] })] }) })] }, s.id))) }) })) }))] }) })] }, "novel-view"));
};
export default Home;
// Category tabs with animated indicator
const CategoryTabs = ({ categories, activeId, onSelect }) => {
    const containerRef = React.useRef(null);
    const itemRefs = React.useRef({});
    const [indicator, setIndicator] = React.useState({ left: 0, width: 0 });
    const measure = React.useCallback((targetIndex) => {
        const container = containerRef.current;
        if (!container)
            return;
        const idx = typeof targetIndex === 'number' ? targetIndex : Math.max(0, categories.findIndex((c) => c.id === activeId));
        const cur = categories[idx];
        if (!cur)
            return;
        const curEl = itemRefs.current[cur.id];
        if (!curEl)
            return;
        const crect = curEl.getBoundingClientRect();
        const contRect = container.getBoundingClientRect();
        const left = crect.left - contRect.left;
        const width = crect.width;
        setIndicator({ left, width });
    }, [categories, activeId]);
    React.useEffect(() => {
        const ro = new ResizeObserver(() => measure());
        const cont = containerRef.current;
        if (cont)
            ro.observe(cont);
        window.setTimeout(() => measure(), 0);
        return () => ro.disconnect();
    }, [measure]);
    // Recalculate indicator when active changes
    React.useEffect(() => {
        measure();
    }, [activeId, categories, measure]);
    return (_jsxs("div", { ref: containerRef, className: "cat-tabs", children: [_jsx("div", { className: "cat-indicator", style: { left: indicator.left, width: indicator.width } }), _jsx(Group, { gap: "xs", wrap: "nowrap", style: { flexWrap: 'nowrap' }, children: categories.map((c) => (_jsx("div", { ref: (el) => { itemRefs.current[c.id] = el; }, onClick: () => onSelect(c.id), style: { flex: '0 0 auto' }, children: _jsx(Badge, { radius: "xl", variant: activeId === c.id ? 'filled' : 'light', style: { cursor: 'pointer' }, children: c.display_name }) }, c.id))) })] }));
};
